import { useMemo, useRef, useState, ReactNode } from "react";
import { flagEmoji } from "../../../../helper/common";
import PublicIcon from "@mui/icons-material/Public";
import {
  RowButton,
  Button,
  Icon,
  Notice,
  OptionCard,
  TextField,
} from "../../../../components/design";
import styles from "../authenticateYourId.module.css";

interface SelectOption {
  value: string;
  labelKey: string;
}

interface CountryOption extends SelectOption {
  name: string;
  flag: string;
}

interface DocMetaEntry {
  title: string;
  hint: string;
  icon: ReactNode;
}

const DOC_META: Record<string, DocMetaEntry> = {
  national_identity_card: {
    title: "National identity card",
    hint: "frontAndBack",
    icon: <Icon name="idCard" />,
  },
  passport: {
    title: "Passport",
    hint: "photoPage",
    // ponytail: no globe/passport icon in the shared Icon set yet
    icon: <PublicIcon fontSize="small" />,
  },
  residence_permit: {
    title: "Residence permit",
    hint: "frontAndBack",
    icon: <Icon name="home" />,
  },
};

type TranslateFn = (key: string) => string;

interface DocumentSelectScreenProps {
  issuingCountry: string;
  docType: string;
  availableCountries: SelectOption[];
  availableDocs: SelectOption[];
  onCountryChange: (country: string) => void;
  onDocTypeChange: (docType: string) => void;
  onContinue: (docType?: string) => void;
  showRetryMessage: boolean;
  _t: TranslateFn;
}

const formatLabel = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getTranslatedText = (
  _t: TranslateFn,
  key: string,
  fallback: string
): string => {
  const translated = _t(key);
  return translated === key ? fallback : translated;
};

const DocumentSelectScreen = ({
  issuingCountry,
  docType,
  availableCountries,
  availableDocs,
  onCountryChange,
  onDocTypeChange,
  onContinue,
  showRetryMessage,
  _t,
}: DocumentSelectScreenProps) => {
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const countryInputRef = useRef<HTMLInputElement>(null);

  const countryOptions: CountryOption[] = useMemo(
    () =>
      availableCountries
        .map((country) => ({
          ...country,
          name: getTranslatedText(
            _t,
            country.labelKey,
            formatLabel(country.labelKey)
          ),
          flag: flagEmoji(country.value),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableCountries, _t]
  );

  const selectedCountry = countryOptions.find(
    (country) => country.value === issuingCountry
  );

  const filteredCountries = countryOptions.filter((country) =>
    country.name.toLowerCase().includes(countrySearch.trim().toLowerCase())
  );

  const handleCountrySelect = (countryValue: string) => {
    onCountryChange(countryValue);
    setCountrySearch("");
    setIsCountryOpen(false);
  };

  const handleCountryFieldClick = () => {
    setCountrySearch(selectedCountry?.name || "");
    setIsCountryOpen(true);
  };

  const handleCountryClear = () => {
    onCountryChange("");
    setCountrySearch("");
    setIsCountryOpen(true);
  };

  const handleDocumentSelect = (documentValue: string) => {
    onDocTypeChange(documentValue);
    onContinue(documentValue);
  };

  return (
    <div className={`${styles.customKycBody} ${styles.customKycDocumentBody}`}>
      <div className={styles.customKycDocumentIntro}>
        <h3>{_t("chooseYourDocument")}</h3>
        <p>{_t("selectIssuingCountryToSeeWhichDocumentsWeAccept")}</p>
      </div>

      {showRetryMessage && (
        <Notice tone="danger" className={styles.customKycDocumentRetryMessage}>
          {_t(
            "previouslyUploadedDocumentsWereNotAcceptedPleaseTryAgainWithNewDocuments"
          )}
        </Notice>
      )}

      <div className={styles.customKycDocumentForm}>
        <div className={styles.customKycCountryField}>
          <label>{_t("issuingCountry")}</label>
          <div
            className={`${styles.customKycCountryCombobox} ${
              isCountryOpen ? "open" : ""
            }`}
          >
            <TextField
              id="issuing-country"
              ref={countryInputRef}
              type="text"
              icon={
                !isCountryOpen && selectedCountry?.flag ? (
                  <span>{selectedCountry.flag}</span>
                ) : undefined
              }
              value={
                isCountryOpen ? countrySearch : selectedCountry?.name || ""
              }
              onChange={(event) => {
                setCountrySearch(event.target.value);
                setIsCountryOpen(true);
              }}
              onClick={handleCountryFieldClick}
              onFocus={handleCountryFieldClick}
              placeholder={_t("selectIssuingCountry")}
              trailing={
                isCountryOpen && countrySearch ? (
                  <Button
                    variant="ghost"
                    iconOnly
                    iconStart={<Icon name="close" />}
                    onClick={handleCountryClear}
                    aria-label={_t("clear")}
                  />
                ) : (
                  <Button
                    variant="ghost"
                    iconOnly
                    iconStart={
                      <Icon
                        name="chevronDown"
                        className={
                          isCountryOpen ? styles.chevronOpen : undefined
                        }
                      />
                    }
                    onClick={() => {
                      setCountrySearch(selectedCountry?.name || "");
                      setIsCountryOpen((current) => !current);
                    }}
                    aria-label={_t("toggleCountryList")}
                  />
                )
              }
            />

            {isCountryOpen && (
              <div className={styles.customKycCountryOptions} role="listbox">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <RowButton
                      type="button"
                      className={styles.customKycCountryOption}
                      key={country.value}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleCountrySelect(country.value)}
                    >
                      {country.flag && (
                        <span className={styles.customKycCountryFlag}>
                          {country.flag}
                        </span>
                      )}
                      <span>{country.name}</span>
                    </RowButton>
                  ))
                ) : (
                  <div className={styles.customKycCountryEmpty}>
                    {_t("noCountriesFound")}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {selectedCountry && (
          <div className={styles.customKycDocumentsSection}>
            <h4>{_t("acceptedDocuments")}</h4>
            <div className={`ds-option-list ${styles.customKycDocumentList}`}>
              {availableDocs.map((document) => {
                const meta = DOC_META[document.value] || {
                  title: formatLabel(document.labelKey),
                  hint: "Front and back",
                  icon: <Icon name="idCard" />,
                };

                return (
                  <OptionCard
                    key={document.value}
                    variant={
                      docType === document.value ? "highlight" : "default"
                    }
                    icon={meta.icon}
                    title={getTranslatedText(_t, document.labelKey, meta.title)}
                    description={_t(meta.hint)}
                    onClick={() => handleDocumentSelect(document.value)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSelectScreen;
